import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import { deleteImpactCo2Answer } from '@/domain/impact-co2/actions/delete-impact-co2-answer.action';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { getImpactCo2AnswersForCategory } from '@/domain/impact-co2/actions/get-impact-co2-answers-for-category.action';
import { submitImpactCo2Answer } from '@/domain/impact-co2/actions/submit-impact-co2-answer.action';
import { submitMultipleImpactCo2Answer } from '@/domain/impact-co2/actions/submit-multiple-impact-co2-answer.action';
import { ImpactCo2Answer } from '@/domain/impact-co2/entities/question.entity';

import { importImpactCo2Answers } from './actions/import-impact-co2-answers.action';

export type QuestionsState = {
  answers: ImpactCo2Answer[];
  isFetchingAnswers: boolean;
  isSubmitingAnswer: boolean;
  isDeletingAnswer: boolean;
  isImportingAnswers: boolean;
};

const initialState: QuestionsState = {
  answers: [],
  isFetchingAnswers: false,
  isSubmitingAnswer: false,
  isDeletingAnswer: false,
  isImportingAnswers: false,
};

export const impactCo2Slice = createSlice({
  name: 'impactCo2',
  initialState,
  reducers: {
    addImpactAnswer(state, action: PayloadAction<ImpactCo2Answer>) {
      state.answers.push(action.payload);
    },
    editImpactAnswer(state, action: PayloadAction<ImpactCo2Answer>) {
      const index = state.answers.findIndex((x) => x.id === action.payload.id);
      state.answers = [...state.answers];
      state.answers[index] = action.payload;
    },
    removeImpactAnswer(state, action: PayloadAction<ImpactCo2Answer>) {
      state.answers = state.answers.filter((x) => x.id !== action.payload.id);
    },
    clearImpactCo2Answers(state) {
      state.answers = [];
    },
  },
  extraReducers(builder) {
    builder
      // answers
      .addCase(getImpactCo2Answers.fulfilled, (state, action) => {
        state.answers = action.payload;
      })
      .addCase(getImpactCo2AnswersForCategory.fulfilled, (state, action) => {
        let newAnswers = [...state.answers];
        newAnswers = newAnswers.filter(
          (x) => x.category !== action.meta.arg.category,
        );
        newAnswers = [...newAnswers, ...action.payload];
        state.answers = newAnswers;
      })
      // isSubmitingAnswer
      .addMatcher(
        isAnyOf(
          submitImpactCo2Answer.pending,
          submitMultipleImpactCo2Answer.pending,
        ),
        (state) => {
          state.isSubmitingAnswer = true;
        },
      )
      .addMatcher(
        isAnyOf(
          submitImpactCo2Answer.fulfilled,
          submitImpactCo2Answer.rejected,
          submitMultipleImpactCo2Answer.fulfilled,
          submitMultipleImpactCo2Answer.rejected,
        ),
        (state) => {
          state.isSubmitingAnswer = false;
        },
      )
      // isFetchingAnswers
      .addMatcher(
        isAnyOf(
          getImpactCo2Answers.pending,
          getImpactCo2AnswersForCategory.pending,
        ),
        (state) => {
          state.isFetchingAnswers = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getImpactCo2Answers.fulfilled,
          getImpactCo2AnswersForCategory.fulfilled,
          getImpactCo2Answers.rejected,
          getImpactCo2AnswersForCategory.rejected,
        ),
        (state) => {
          state.isFetchingAnswers = false;
        },
      )
      // isDeletingAnswer
      .addMatcher(isAnyOf(deleteImpactCo2Answer.pending), (state) => {
        state.isDeletingAnswer = true;
      })
      .addMatcher(
        isAnyOf(
          deleteImpactCo2Answer.fulfilled,
          deleteImpactCo2Answer.rejected,
        ),
        (state) => {
          state.isDeletingAnswer = false;
        },
      )
      // isImportingAnswers
      .addMatcher(isAnyOf(importImpactCo2Answers.pending), (state) => {
        state.isImportingAnswers = true;
      })
      .addMatcher(
        isAnyOf(
          importImpactCo2Answers.fulfilled,
          importImpactCo2Answers.rejected,
        ),
        (state) => {
          state.isImportingAnswers = false;
        },
      );
  },
});
